import {
  priceLine,
  stanleyPrice,
  solsPrice,
  defaultSelections,
  describeSelections,
  findTech,
  formatEur,
} from '@theme/supplier-pricing';

const EXAMPLE_QTYS = [1, 10, 50, 100, 250];

/**
 * Product page price + print options for supplier-priced products
 * (blocks/supplier-print-options.liquid). Midocean: customer picks print positions,
 * method per position and logo colours; price recalculates for the exact quantity.
 * Stanley/Stella and SOL'S: price only (tier price + supplier delivery spread over the quantity).
 *
 * Writes two line-item properties into the product form:
 *   Apdruka  - readable description (shows in cart, request email, draft order)
 *   _print   - selections JSON, read by the cart and the request service to price the line
 */
class SupplierPrintOptions extends HTMLElement {
  connectedCallback() {
    this.supplier = this.dataset.supplier;
    this.form = document.getElementById(this.dataset.formId);
    this.min = parseInt(this.dataset.min, 10) || 1; // custom.min_order_qty (2026-10-30)
    this.config = this.#json('[data-config]');
    this.variants = this.#json('[data-variants]') || {};
    this.selections = this.config ? defaultSelections(this.config) : [];

    if (this.supplier === 'midocean' && this.config) this.#renderOptions();
    this.update();

    this.abort = new AbortController();
    const { signal } = this.abort;
    const onChange = (event) => {
      const t = event.target;
      if (t && this.form && (t.form === this.form || t.closest?.('product-form-component'))) this.update();
    };
    document.addEventListener('input', onChange, { signal });
    document.addEventListener('change', onChange, { signal });
    document.addEventListener('quantity-selector:update', () => this.update(), { signal });
    document.addEventListener('variant:update', () => setTimeout(() => this.update(), 0), { signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  #json(selector) {
    const el = this.querySelector(selector);
    try {
      return el ? JSON.parse(el.textContent) : null;
    } catch {
      return null;
    }
  }

  get quantity() {
    const q = parseInt(this.form?.elements.quantity?.value, 10);
    return Math.max(q > 0 ? q : 1, this.min);
  }

  get variant() {
    const id = this.form?.elements.id?.value;
    return this.variants[id] || Object.values(this.variants)[0] || {};
  }

  #price(qty) {
    if (this.supplier === 'midocean') return priceLine(this.config, this.variant.sku, qty, this.selections);
    const tiers = this.variant.tiers;
    if (!tiers || !tiers.length) return null;
    return this.supplier === 'sols' ? solsPrice(tiers, qty) : stanleyPrice(tiers, qty);
  }

  update() {
    const qty = this.quantity;
    const price = this.#price(qty);
    if (!price) return;
    this.querySelector('[data-pc]').textContent = formatEur(price.pc);
    this.querySelector('[data-qty]').textContent = String(qty);
    this.querySelector('[data-total]').textContent = formatEur(price.total);
    this.querySelector('[data-examples]').innerHTML = EXAMPLE_QTYS.filter((q) => q >= this.min).map(
      (q) => `<li><span>${q} gab.</span><b>${formatEur(this.#price(q).pc)}</b></li>`,
    ).join('');

    if (this.supplier === 'midocean') {
      this.querySelector('[data-print-json]').value = JSON.stringify(this.selections);
      this.querySelector('[data-print-text]').value = describeSelections(this.config, this.selections);
      this.#renderSelected();
    }
  }

  /* ---------- Midocean print options ---------- */

  #renderOptions() {
    const panel = this.querySelector('[data-panel]');
    panel.innerHTML = this.config.positions
      .map((pos, i) => {
        const methods = pos.techs
          .map((t) => `<option value="${t.id}"${t.default ? ' selected' : ''}>${t.name}</option>`)
          .join('');
        return `
          <div class="spo__position">
            <label class="spo__position-check">
              <input type="checkbox" data-pos="${i}"${i === 0 ? ' checked' : ''}>
              ${pos.img ? `<img src="${pos.img}" alt="" width="56" height="56" loading="lazy">` : ''}
              <span>${pos.name}</span>
            </label>
            ${
              pos.techs.length > 1
                ? `<select class="spo__select" data-method="${i}" aria-label="Apdrukas metode: ${pos.name}">${methods}</select>`
                : `<span class="spo__single-method">${pos.techs[0].name}</span>`
            }
          </div>`;
      })
      .join('');

    panel.addEventListener('change', (event) => {
      const t = event.target;
      if (t.dataset.pos !== undefined) {
        const checked = [...panel.querySelectorAll('[data-pos]:checked')];
        if (!checked.length) t.checked = true; // at least one print position
      }
      this.#readPanel();
      this.update();
    });

    const toggle = this.querySelector('[data-toggle]');
    toggle.addEventListener('click', () => {
      const open = panel.hidden;
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
      toggle.textContent = open ? 'Paslēpt apdrukas iespējas' : 'Mainīt apdruku';
    });
    if (this.config.positions.length === 1 && this.config.positions[0].techs.length === 1) toggle.hidden = true;
  }

  #readPanel() {
    const panel = this.querySelector('[data-panel]');
    const previous = new Map(this.selections.map((s) => [s.pos, s]));
    this.selections = [...panel.querySelectorAll('[data-pos]:checked')].map((box) => {
      const pos = this.config.positions[box.dataset.pos];
      const select = panel.querySelector(`[data-method="${box.dataset.pos}"]`);
      const techId = select ? select.value : pos.techs[0].id;
      const prev = previous.get(pos.id);
      return { pos: pos.id, tech: techId, colours: prev && prev.tech === techId ? prev.colours : 1 };
    });
  }

  #renderSelected() {
    const list = this.querySelector('[data-selected]');
    const focused = document.activeElement?.dataset?.colours;
    list.innerHTML = this.selections
      .map((sel, i) => {
        const { pos, tech } = findTech(this.config, sel);
        const colourSelect =
          tech.colour && tech.max > 1
            ? `<label class="spo__colours">Logo krāsu skaits
                 <select class="spo__select" data-colours="${i}">
                   ${Array.from({ length: tech.max }, (_, k) => k + 1)
                     .map((n) => `<option value="${n}"${n == sel.colours ? ' selected' : ''}>${n}</option>`)
                     .join('')}
                 </select>
               </label>`
            : '';
        return `<li class="spo__selected-row">
                  <span class="spo__selected-name">
                    ${pos.img ? `<img src="${pos.img}" alt="Apdrukas vieta: ${pos.name}" width="56" height="56">` : ''}
                    <span>
                      <small>Apdrukas vieta</small> <b>${pos.name}</b><br>
                      <small>Metode</small> ${tech.name}${tech.colour && tech.max === 1 ? ' (1 krāsa)' : ''}
                    </span>
                  </span>${colourSelect}</li>`;
      })
      .join('');
    list.querySelectorAll('[data-colours]').forEach((select) => {
      select.addEventListener('change', () => {
        this.selections[select.dataset.colours].colours = parseInt(select.value, 10);
        this.update();
        this.querySelector(`[data-colours="${select.dataset.colours}"]`)?.focus();
      });
    });
    if (focused !== undefined) this.querySelector(`[data-colours="${focused}"]`)?.focus();
    this.querySelector('[data-hint]').hidden = !list.querySelector('[data-colours]');
  }
}

if (!customElements.get('supplier-print-options')) {
  customElements.define('supplier-print-options', SupplierPrintOptions);
}
